import { redirect } from '@tanstack/react-router'
import { ApiError } from './api-fetch.ts'

/**
 * Run a route loader's data fetching, turning "no session" into the sign-in
 * page instead of an error boundary.
 *
 * Every gated loader used to hand-roll the same try/catch — `if (err
 * instanceof ApiError && err.status === 401) throw redirect(...)` — each one
 * carrying its own copy of the eslint-disable below, which is exactly the
 * sort of comment that rots in thirteen places at once. The wrapper takes a
 * thunk rather than a promise so the fetch starts inside the guard; passing
 * an already-started promise would work too, but a thunk reads the same at
 * every call site whether the loader awaits one call or several.
 *
 * Note that a 401 here includes the ordinary SSR case: the server-side
 * loader fetch carries no browser cookie unless `buildApiHeaders` could
 * forward one, so an unauthenticated first paint lands on /login.
 *
 * Not every loader wants this. `routes/index.tsx` deliberately swallows its
 * 401 — it is the landing page and must always render — so it keeps its own
 * catch and does not use this guard.
 */
export async function withSessionRedirect<T>(
  load: () => Promise<T>,
): Promise<T> {
  try {
    return await load()
  } catch (err) {
    if (err instanceof ApiError && err.status === 401)
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router's documented redirect pattern throws a Redirect object
      throw redirect({ to: '/login' })
    throw err
  }
}
